import { API_HEADERS, CONTENT_TYPES } from '@/components/emotion-detector/constants';
import { ApiRequest } from '@/hooks/api/useApi';

interface GuideAsset {
	url: string;
	type: string;
	duration?: number;
}

interface Guide {
	id?: string;
	title: string;
	cell_image?: { url: string };
	asset: GuideAsset;
}

interface ProgramData {
	id?: string;
	title?: string;
	background_image?: { url: string };
	guides?: Guide[];
}

interface GuideApiResponse {
	program?: ProgramData;
}

interface ContentPlayData {
	audioUrl: string;
	title: string;
	thumbnailUrl?: string;
	programTitle?: string;
}

/**
 * Fetches guide data from API
 */
export async function fetchGuideData(guideId: string, apiRequest: ApiRequest): Promise<GuideApiResponse> {
	return apiRequest<GuideApiResponse>({
		endpoint: `content/programs/guides/${guideId}`,
		method: 'GET',
		customHeaders: {
			'x-device-capabilities': API_HEADERS.DEVICE_CAPABILITIES,
		},
	}).then(res => res.data);
}

/**
 * Fetches program data from API
 */
export async function fetchProgramData(programId: string, apiRequest: ApiRequest): Promise<GuideApiResponse> {
	return apiRequest<GuideApiResponse>({
		endpoint: `content/programs/${programId}`,
		method: 'GET',
		customHeaders: {
			'x-device-capabilities': API_HEADERS.DEVICE_CAPABILITIES,
		},
	}).then(res => res.data);
}

/**
 * Extracts playable content data from guide API response
 */
export function extractGuidePlayData(
	apiResponse: GuideApiResponse,
	fallbackImageUrl?: string,
): ContentPlayData | null {
	const program = apiResponse.program;
	const firstGuide = program?.guides?.[0];
	const asset = firstGuide?.asset;

	if (!program || !firstGuide || !asset) {
		return null;
	}

	// Skip video content - emotion detector only supports audio
	if (asset.type === CONTENT_TYPES.VIDEO) {
		return null;
	}

	const thumbnailUrl = program.background_image?.url || firstGuide.cell_image?.url || fallbackImageUrl;

	return {
		audioUrl: asset.url,
		title: firstGuide.title || 'Audio Content',
		thumbnailUrl,
		programTitle: program.title,
	};
}

/**
 * Extracts playable content data from program API response
 */
export function extractProgramPlayData(
	apiResponse: GuideApiResponse,
	fallbackTitle: string,
	fallbackImageUrl?: string,
): ContentPlayData | null {
	const program = apiResponse.program;
	const firstGuide = program?.guides?.[0];
	const asset = firstGuide?.asset;

	if (!program || !firstGuide || !asset) {
		return null;
	}

	// Skip video content - emotion detector only supports audio
	if (asset.type === CONTENT_TYPES.VIDEO) {
		return null;
	}

	const thumbnailUrl = program.background_image?.url || firstGuide.cell_image?.url || fallbackImageUrl;

	return {
		audioUrl: asset.url,
		title: firstGuide.title || fallbackTitle,
		thumbnailUrl,
		programTitle: program.title || fallbackTitle,
	};
}
